import { useEffect } from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { Get } from "../../../../connection/helpers";
import { EventsState } from "../../../../store/types";
import { AnalyticsEvent, AnalyticsSession } from "../../../../types/working-model";
import { showAlert } from "../../../general-ui/alert/alert-service";
import { Button } from "../../../general-ui/button/button";
import ModalComponent from "../../../general-ui/modal/modal";
import Switch from "../../../general-ui/switch/switch";
import WaitingIndicator from "../../../general-ui/waiting-indicator/waiting-indicator";

interface props {
	open: boolean;
	onClose: () => void;
	event: AnalyticsEvent;
	session?: AnalyticsSession;
}

interface report {
	name: string;
	url: string;
	selected: boolean;
}

const eventReports = [
	{ name: 'Registrations', selected: true, url: '/registrations' },
	{ name: 'Page Views', selected: true, url: '/views' },
	{ name: 'Chat', selected: true, url: '/chat' },
	{ name: 'Summary', selected: true, url: '/summary' }
];

const sessionReports = [
	{ name: 'Registrations', selected: true, url: '/registrations' },
	{ name: 'Page Views', selected: true, url: '/views' },
	{ name: 'Chat', selected: true, url: '/chat' },
	{ name: 'Summary', selected: true, url: '/summary' }
];

function getBaseExportUrl(event: string, session?: string): string {
	if (session) {
		return '/v3/admin/metrics/sessions/' + session + '/export';
	}
	return '/v3/admin/metrics/events/' + event + '/export';
}

export function AnalyticsExportModal({ open, onClose, event, session }: props): JSX.Element {
	const {
		AuthReducer: { token },
	} = useSelector((state: EventsState) => state);

	const [reports, setReports] = useState<Array<report>>(session ? sessionReports : eventReports);
	const [downloading, setDownloading] = useState<boolean>(false);

	useEffect(() => {
		setReports(session ? sessionReports : eventReports);
	}, [session]);

	const downloadSelected = () => {
		const baseExportUrl = getBaseExportUrl(event?.uuid, session?.uuid);
		setDownloading(true);

		const date = new Date();
		const exportDate = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

		const selectedReports = reports.filter(report => report.selected);
		const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
		let downloadCount = 0;

		for (const report of selectedReports) {

			const eventOrSessionName = (session ? session?.title?.base : event.name) || '';

			Get(`${baseExportUrl}${report.url}?timeZone=${timeZone}`, token!, {}, true)
				.then(resp => {
					if (resp.ok) {
						return resp.blob();
					}
					throw ('Unable to fetch report');
				})
				.then(blob => {
					const url = window.URL.createObjectURL(blob);
					const a = document.createElement('a');
					a.style.display = 'none';
					a.href = url;
					a.download = `${eventOrSessionName} ${report.name}-${exportDate}.csv`.trim();
					document.body.appendChild(a);
					a.click();
					window.URL.revokeObjectURL(url);

					downloadCount++;
					if (downloadCount === selectedReports.length) {
						setDownloading(false);
					}
				})
				.catch(() => {
					setDownloading(false);
					showAlert({ message: "Download failed", description: "Some or all of your reports failed to generate", duration: 3000, type: "error" });
				});
		}
	};

	const toggleReport = (report: report) => {
		const newReports = [...reports];
		const newReport = reports.find(re => re.name === report.name);
		if (newReport) {
			newReport.selected = !newReport.selected;
		}
		setReports(newReports);
	};

	return (
		<ModalComponent
			title="Custom Report"
			subtitle="Select the reports you want to download"
			open={open}
			onRequestClose={onClose}
			closeable
			cancellable
			footer={
				<div>
					<Button onClick={onClose}>Cancel</Button>
					<Button type="primary" onClick={downloadSelected} isDisabled={downloading}>{downloading ? <WaitingIndicator /> : 'Download'}</Button>
				</div>
			}
		>

			<div className="export-modal-option-container">
				{reports.map(report => (
					<div className="item" key={report.name}>
						<div className="field-group switch">
							<Switch on={report.selected} onClick={() => toggleReport(report)} value={report.selected} inline={true} /><label>{report.name}</label>
						</div>
					</div>
				))
				}
			</div>
		</ModalComponent>
	);
}